import { authStore } from "@kaplan-labs/up-auth-api-client";

const CONSENT_CATEGORY = ",C0002,";

type HeapState = "idle" | "loading" | "active" | "suspended" | "failed";

let state: HeapState = "idle";
let heapId: string | undefined;
let queue: Array<{ method: "track" | "identify"; args: unknown[] }> = [];
let authUnsubscribe: (() => void) | undefined;
let consentListener: (() => void) | undefined;
// Whether the heap-<id>.js bundle has finished executing.
let scriptSettled = false;
// Set when consent is revoked while the bundle is still in flight, so the
// suspension is applied once the bundle finishes executing.
let pendingSuspend = false;

function hasConsent(): boolean {
  const groups = window.OnetrustActiveGroups;
  if (typeof groups !== "string") return false;
  return groups.includes(CONSENT_CATEGORY);
}

function injectScript(id: string): void {
  // Heap's standard loader pattern: an array that doubles as a queue.
  // Method calls are pushed as sub-arrays until the real SDK replaces this stub.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const heap = [] as any;
  heap.appid = id;
  heap.config = {};

  const methods = [
    "addEventProperties",
    "addUserProperties",
    "clearEventProperties",
    "identify",
    "resetIdentity",
    "removeEventProperty",
    "setEventProperties",
    "track",
    "unsetEventProperty",
  ];

  for (const method of methods) {
    heap[method] = (...args: unknown[]) => {
      heap.push([method, ...args]);
    };
  }

  window.heap = heap;

  const script = document.createElement("script");
  script.id = "heap-script";
  script.type = "text/javascript";
  script.async = true;
  script.src = `https://cdn.heapanalytics.com/js/heap-${id}.js`;
  script.addEventListener("load", handleScriptLoad);
  script.addEventListener("error", handleScriptError);
  document.head.appendChild(script);

  heap.load?.(id);
}

// Runs once the SDK bundle has finished executing. At this point window.heap is
// the real SDK, so any suspension deferred while the bundle was in flight can
// now be applied.
function handleScriptLoad(): void {
  scriptSettled = true;

  if (pendingSuspend) {
    pendingSuspend = false;
    suspend();
    return;
  }

  if (state === "loading") {
    state = "active";
  }
}

// The heap-<id>.js bundle failed to download or execute (network error, 404, or
// an adblocker blocking cdn.heapanalytics.com). The real SDK will never replace
// the stub, so there is nothing to activate or suspend. Tear down the auth
// listener, drop anything queued, and move to a terminal state. Later consent
// changes intentionally do NOT retry a script that just failed.
function handleScriptError(): void {
  scriptSettled = true;
  pendingSuspend = false;
  stopAuthListener();
  queue = [];
  state = "failed";
}

function flushQueue(): void {
  for (const entry of queue) {
    if (entry.method === "track" && window.heap) {
      window.heap.track(
        entry.args[0] as string,
        entry.args[1] as Record<string, unknown> | undefined,
      );
    } else if (entry.method === "identify" && window.heap) {
      window.heap.identify(entry.args[0] as string);
    }
  }
  queue = [];
}

function startAuthListener(): void {
  const identifyCurrentUser = () => {
    const $auth = authStore.get();
    if (window.heap && $auth.currentUser) {
      window.heap.identify($auth.currentUser.contactId as string);
    }
  };

  authUnsubscribe = authStore.listen(() => {
    identifyCurrentUser();
  });

  identifyCurrentUser();
}

function stopAuthListener(): void {
  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = undefined;
  }
}

// Heap gates its network/beacon sender on `window.heap.loaded`, so toggling the
// flag disables (false) or resumes (true) tracking without unloading the SDK.
function setHeapLoaded(loaded: boolean): void {
  if (window.heap) {
    (window.heap as { loaded?: boolean }).loaded = loaded;
  }
}

function load(): void {
  if (!heapId) return;

  // Consent was re-granted after a suspension. The SDK is already present, so
  // re-enable it in place rather than injecting a second copy (which would
  // register a duplicate set of Heap listeners).
  if (state === "suspended") {
    pendingSuspend = false;
    setHeapLoaded(true);
    state = scriptSettled ? "active" : "loading";
    flushQueue();
    startAuthListener();
    return;
  }

  if (state !== "idle") return;

  state = "loading";
  injectScript(heapId);
  flushQueue();
  startAuthListener();
}

function suspend(): void {
  // Heap registers persistent listeners and timers (beforeunload, message,
  // scroll/mouse/touch handlers, a polling loop, cookie reads) that all read
  // `window.heap.loaded`. Removing the <script> element does not detach them,
  // and deleting window.heap makes them throw
  // "Cannot read properties of undefined (reading 'loaded')" the next time they
  // fire. So instead of unloading, we neutralize in place: flip the `loaded`
  // kill-switch to stop Heap's request pathway, then drop the current identity
  // and event properties.
  setHeapLoaded(false);

  if (window.heap) {
    window.heap.resetIdentity();
    window.heap.clearEventProperties();
  }

  stopAuthListener();
  queue = [];
  state = "suspended";
}

function teardown(): void {
  if (state !== "loading" && state !== "active") return;

  // The SDK bundle is still downloading/executing. Neutralizing now would be
  // undone when the bundle overwrites window.heap, so defer the suspension
  // until the script settles. window.heap is left in place so the in-flight
  // bundle can read window.heap.loaded without throwing.
  if (!scriptSettled) {
    pendingSuspend = true;
    stopAuthListener();
    queue = [];
    state = "suspended";
    return;
  }

  suspend();
}

function onConsentChange(): void {
  if (hasConsent()) {
    load();
  } else {
    teardown();
  }
}

export function heapLoaderInit(id?: string): void {
  if (!id) return;

  // Clean up any existing listener first (including from previous module instances)
  if (consentListener) {
    window.removeEventListener("OneTrustGroupsUpdated", consentListener);
  }
  // Also check for listeners from previous module instances
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalListener = (window as any).__heapConsentListener;
  if (globalListener) {
    window.removeEventListener("OneTrustGroupsUpdated", globalListener);
  }

  heapId = id;
  state = "idle";
  queue = [];
  scriptSettled = false;
  pendingSuspend = false;

  if (hasConsent()) {
    load();
  }

  // Store the listener reference so we can clean it up
  consentListener = onConsentChange;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__heapConsentListener = consentListener;
  window.addEventListener("OneTrustGroupsUpdated", consentListener);
}

export function heapTrack(
  event: string,
  payload?: Record<string, unknown>,
): void {
  if (!heapId || state === "failed") return;

  if ((state === "active" || state === "loading") && window.heap) {
    window.heap.track(event, payload);
  } else {
    queue.push({ method: "track", args: [event, payload] });
  }
}

export function heapIdentify(contactId: string): void {
  if (!heapId || state === "failed") return;

  if ((state === "active" || state === "loading") && window.heap) {
    window.heap.identify(contactId);
  } else {
    queue.push({ method: "identify", args: [contactId] });
  }
}
